import type { PulseClientConfig } from "./config";
import { PulseError } from "./errors";
import { generateEventId, nowIso } from "./ids";

export type PulseEventName =
  | "content_view"
  | "contact_intent"
  | "marketplace_search"
  | "entity_impression"
  | "user_action";

export const PULSE_EVENT_NAMES: readonly PulseEventName[] = [
  "content_view",
  "contact_intent",
  "marketplace_search",
  "entity_impression",
  "user_action",
];

export type TrackOptions = {
  eventId?: string;
  tenantId?: string;
  occurredAt?: Date | string;
  anonymousId?: string | null;
  userId?: string | null;
  metadata?: Record<string, unknown>;
  immediate?: boolean;
};

export type PulseEventPayload = {
  event_id: string;
  event_name: PulseEventName;
  application_id: string;
  tenant_id: string;
  occurred_at: string;
  anonymous_id: string | null;
  user_id: string | null;
  metadata: Record<string, unknown>;
};

export const MAX_EVENTS_PER_BATCH = 50;

export function buildEventPayload(
  config: PulseClientConfig,
  eventName: PulseEventName,
  options: TrackOptions = {},
): PulseEventPayload {
  const tenantId = options.tenantId ?? config.tenantId;
  if (!tenantId) {
    throw new PulseError(
      0,
      "invalid_request",
      "tenantId is required: pass options.tenantId or config.tenantId",
    );
  }

  const anonymousId =
    options.anonymousId !== undefined
      ? options.anonymousId
      : (config.anonymousId ?? config.getAnonymousId?.() ?? null);

  const userId = options.userId !== undefined ? options.userId : (config.userId ?? null);

  const occurredAt =
    options.occurredAt instanceof Date ? options.occurredAt.toISOString() : (options.occurredAt ?? nowIso());

  return {
    event_id: options.eventId ?? generateEventId(),
    event_name: eventName,
    application_id: config.applicationId,
    tenant_id: tenantId,
    occurred_at: occurredAt,
    anonymous_id: anonymousId,
    user_id: userId,
    metadata: options.metadata ?? {},
  };
}

export function chunkEvents<T>(events: T[], size: number = MAX_EVENTS_PER_BATCH): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < events.length; index += size) {
    chunks.push(events.slice(index, index + size));
  }
  return chunks;
}
